import {
  IUser,
  IUserLoginPayload,
  IUserLoginResponse,
} from "../../../types/api/user";
import { TResponse } from "../../../types/common";
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  getUserInfo,
  userLogin,
  userUpdatedProfile,
  adminUpdatePassword,
  validateToken,
} from "./callbacks";
import { message } from "antd";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
// import commonObj from "@src/commonObj";

export const useUserLogin = (
  options: UseMutationOptions<
    TResponse<IUserLoginResponse>,
    Error,
    IUserLoginPayload
  > = {}
): UseMutationResult<
  TResponse<IUserLoginResponse>,
  Error,
  IUserLoginPayload
> => {
  return useMutation({
    mutationFn: async (payload: IUserLoginPayload) => {
      const response = await userLogin(payload);
      return response.data;
    },
    onSuccess: (res) => {
      message.success(res.message);
    },
    onError: (error: Error) => {
      message.error(error.message);
    },
    ...options,
  });
};
export const useGetUserInfo = (
  options: Omit<
    UseQueryOptions<TResponse<IUser | null>, Error>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TResponse<IUser | null>, Error> => {
  const queryResult = useQuery({
    queryKey: [API_ENDPOINTS.ADMIN_DETAIL],
    queryFn: async () => {
      const res = await getUserInfo();
      return res.data;
    },
    initialData: { data: null, success: false, message: "", code: 500 },
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
  };
};

export const useValidateToken = (
  options: Omit<
    UseQueryOptions<TResponse<IUser | null>, Error>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TResponse<IUser | null>, Error> => {
  const queryResult = useQuery({
    queryKey: [API_ENDPOINTS.VALIDATE_TOKEN],
    queryFn: async () => {
      const res = await validateToken();
      return res.data;
    },
    initialData: { data: null, success: false, message: "", code: 500 },
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    // retry : false,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
  };
};

export const useUserProfileUpdate = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IUser>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IUser>, unknown, IUser> = {}
): UseMutationResult<TResponse<IUser>, unknown, IUser> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IUser>, unknown, IUser>({
    mutationFn: async (data: any) => {
      const response = await userUpdatedProfile(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.ADMIN_DETAIL],
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useAdminPasswordUpdate = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IUser>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IUser>, unknown, IUser> = {}
): UseMutationResult<TResponse<IUser>, unknown, IUser> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IUser>, unknown, IUser>({
    mutationFn: async (data: any) => {
      const response = await adminUpdatePassword(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.ADMIN_DETAIL],
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};
